<script>
import appConfig from '@src/app.config'
import Layout from '@layouts/main'
import PageHeader from '@components/page-header'
import axios from 'axios'
import VueApexCharts from 'vue-apexcharts'
import html2pdf from 'html2pdf.js'

export default {
  page: {
    title: 'Tenable Vulnerabilities',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  components: {
    Layout,
    PageHeader,
    apexchart: VueApexCharts
  },
  data () {
    return {
      updated: '',
      vulnerabilities: [],
      filteredVulnerabilities: [],
      title: 'Tenable Vulnerabilities',
      loading: true,
      chartOptions: {
        chart: {
          type: 'bar',
          height: 350,
          toolbar: {
            show: false
          },
          fontFamily: 'inherit'
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded',
            distributed: true,  // Enable distributed mode for individual colors
            dataLabels: {
              position: 'top'
            }
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val
          },
          offsetY: -20,
          style: {
            fontSize: '12px',
            colors: ['#304758']
          }
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        xaxis: {
          categories: [],
          title: {
            text: 'Vulnerabilities'
          },
          labels: {
            style: {
              colors: [],  // Will be set dynamically
              fontSize: '12px',
              fontWeight: 600
            },
            rotate: -45,
            rotateAlways: true,
            trim: false,
            minHeight: 80
          }
        },
        yaxis: {
          title: {
            text: 'Count'
          }
        },
        fill: {
          opacity: 1
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + " instances"
            }
          }
        },
        colors: [],
        legend: {
          show: false
        }
      },
      chartSeries: [],
      // Search filters
      filters: {
        search: ''
      },
      // Selected severity levels for multi-select
      selectedSeverities: [],
      // Severity options for checkboxes
      severityOptions: [
        { value: 'Critical', text: 'Critical', numeric: 4 },
        { value: 'High', text: 'High', numeric: 3 },
        { value: 'Medium', text: 'Medium', numeric: 2 },
        { value: 'Low', text: 'Low', numeric: 1 },
        { value: 'Info', text: 'Info', numeric: 0 }
      ],
      // Date range selection
      selectedDateRange: '7',
      // Date range options
      dateRangeOptions: [
        { value: '7', text: 'Last 7 Days' },
        { value: '15', text: 'Last 15 Days' },
        { value: '30', text: 'Last 30 Days' },
        { value: '60', text: 'Last 60 Days' },
        { value: '120', text: 'Last 120 Days' },
        { value: '365', text: 'Last 365 Days' },
        { value: '720', text: 'Last 720 Days' }
      ],
      // Sorting
      sortKey: 'count',
      sortDirection: 'desc',
      // Column headers for the table
      columns: [
        {
          key: 'plugin_name',
          label: 'Vulnerability',
          sortable: true
        },
        {
          key: 'plugin_family',
          label: 'Family',
          sortable: true
        },
        {
          key: 'severity',
          label: 'Severity',
          sortable: true
        },
        {
          key: 'count',
          label: 'Count',
          sortable: true
        },
        {
          key: 'vulnerability_state',
          label: 'State',
          sortable: true
        },
        {
          key: 'cvss_base_score',
          label: 'CVSS Score',
          sortable: true
        },
        {
          key: 'cvss3_base_score',
          label: 'CVSS3 Score',
          sortable: true
        }
      ],
      items: [
        {
          text: 'Home',
          href: '/'
        },
        {
          text: 'Tenable',
          href: '/tenable'
        },
        {
          text: 'Vulnerabilities',
          active: true
        }
      ],
      appConfig: process.env.NODE_ENV === 'production' ? require('@src/app.config') : require('@src/app.config.dev'),
      pdfConfig: {
        margin: 0.2,
        filename: 'Tenable Scan Report - Corona.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, dpi: 96, letterRendering: true, width: 1280 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' },
        pagebreak: { mode: 'css', before: '#page2el' },
        _pageBreak: { mode: 'avoid-all', before: '.break-page', after: '.break-page' }
      },
      // Modal state and vulnerability details
      showVulnerabilityModal: false,
      selectedVulnerability: null,
      vulnerabilityDetails: null,
      loadingVulnerabilityDetails: false
    }
  },
  mounted () {
    // Select all severities by default
    // this.selectedSeverities = this.severityOptions.map(option => option.value)
    this.selectedSeverities = this.severityOptions.filter(option => option.numeric === 4).map(option => option.value)
    this.loadData()
  },

  watch: {
    // Watch for changes in filters and update filtered data
    filters: {
      handler() {
        this.applyFilters()
      },
      deep: true
    },
    // Update filtered data when raw data changes
    vulnerabilities: {
      handler() {
        this.applyFilters()
      },
      immediate: true
    },
    // Reload data when severity selection changes
    selectedSeverities: {
      handler() {
        this.loadData()
      }
    },
    // Reload data when date range changes
    selectedDateRange: {
      handler() {
        this.loadData()
      }
    }
  },
  methods: {
    loadData () {
      let that = this
      let baseURL = that.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
      
      // Build params object
      let params = {
        'date_range': this.selectedDateRange
      }
      
      // Add severity filters if any are selected
      if (this.selectedSeverities.length > 0) {
        params['filter.0.filter'] = 'severity'
        params['filter.0.quality'] = 'match'
        params['filter.0.value'] = this.selectedSeverities.join(',')
      }

      // Convert params to URL query string
      const queryString = Object.keys(params)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&')
      
      const url = `${baseURL}/tenable/vulnerabilities?${queryString}`
      
      that.loading = true

      axios.get(url)
        .then((response) => {
          console.log('response vulnerabilities', response)
          // Check if response.data exists and has the expected format
          if (response.data && response.data.vulnerabilities && Array.isArray(response.data.vulnerabilities)) {
            that.vulnerabilities = response.data.vulnerabilities
          } else {
            console.error('Unexpected API response format:', response.data)
            that.vulnerabilities = []
          }
          const now = new Date()
          that.updated = now.toLocaleString()
          that.loading = false
          that.applyFilters() // Apply filters to the loaded data
          that.renderChart() // Render the chart with the data
        })
        .catch(function (error) {
          console.error('Error fetching vulnerability data:', error)
          that.vulnerabilities = []
          that.loading = false
        })
    },
    
    // Map severity names to their numeric values for API call
    mapSeverityNamesToNumbers() {
      const severityMap = {
        'Critical': '4',
        'High': '3',
        'Medium': '2',
        'Low': '1',
        'Info': '0'
      }
      
      return this.selectedSeverities.map(name => severityMap[name] || name)
    },
    
    // Render chart with top 10 vulnerabilities
    renderChart() {
      // Get top 10 vulnerabilities by count
      const top10 = [...this.filteredVulnerabilities]
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)
      
      if (top10.length === 0) return
      
      // Prepare data for ApexCharts
      const categories = top10.map(vuln => this.truncateText(vuln.plugin_name, 25))
      const counts = top10.map(vuln => vuln.count)
      const colors = top10.map(vuln => this.getSeverityBackgroundColor(vuln.severity))
      const textColors = top10.map(vuln => this.getSeverityTextColor(vuln.severity))
      
      // Sort vulnerabilities by severity (Critical to Info) then by count
      const sortedData = [...top10]
        .sort((a, b) => {
          // First sort by severity (higher severity first)
          if (a.severity !== b.severity) {
            return b.severity - a.severity
          }
          // Then by count (higher count first)
          return b.count - a.count
        })
      
      const sortedCategories = sortedData.map(vuln => this.truncateText(vuln.plugin_name, 25))
      const sortedCounts = sortedData.map(vuln => vuln.count)
      const sortedColors = sortedData.map(vuln => this.getSeverityBackgroundColor(vuln.severity))
      const sortedTextColors = sortedData.map(vuln => this.getSeverityTextColor(vuln.severity))
      
      // Update chart options and series
      this.chartOptions = {
        ...this.chartOptions,
        xaxis: {
          ...this.chartOptions.xaxis,
          categories: sortedCategories,
          labels: {
            ...this.chartOptions.xaxis.labels,
            style: {
              ...this.chartOptions.xaxis.labels.style,
              colors: sortedTextColors
            }
          }
        },
        colors: sortedColors,
        tooltip: {
          y: {
            formatter: function(val) {
              return val + " instances"
            }
          },
          custom: ({ series, seriesIndex, dataPointIndex }) => {
            const vuln = sortedData[dataPointIndex]
            const severityText = this.getSeverityText(vuln.severity)
            const severityColor = this.getSeverityBackgroundColor(vuln.severity)
            
            return `<div class="apexcharts-tooltip-box">
              <div class="apexcharts-tooltip-title" style="color:${severityColor}">${vuln.plugin_name}</div>
              <div>Family: ${vuln.plugin_family}</div>
              <div>Count: ${vuln.count}</div>
              <div>Severity: <span style="color:${severityColor};font-weight:bold">${severityText}</span></div>
            </div>`
          }
        }
      }
      
      this.chartSeries = [{
        name: 'Vulnerability Count',
        data: sortedCounts
      }]
    },
    
    // Truncate text with ellipsis if too long
    truncateText(text, maxLength) {
      if (!text) return ''
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
    },
    

    
    // Apply filters to the vulnerability data
    applyFilters() {
      let result = [...this.vulnerabilities]
      
      // Filter by search term
      if (this.filters.search) {
        const searchTerm = this.filters.search.toLowerCase()
        result = result.filter(item => {
          return (
            (item.plugin_name && item.plugin_name.toLowerCase().includes(searchTerm)) ||
            (item.plugin_family && item.plugin_family.toLowerCase().includes(searchTerm))
          )
        })
      }
      
      // Apply sorting if a sort key is set
      if (this.sortKey) {
        result = this.sortData(result, this.sortKey, this.sortDirection)
      }
      
      this.filteredVulnerabilities = result
    },
    
    // Sort the data by a specific key and direction
    sortData(data, key, direction) {
      return [...data].sort((a, b) => {
        let aValue = a[key] !== undefined ? a[key] : ''
        let bValue = b[key] !== undefined ? b[key] : ''
        
        // Handle numeric sorting
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return direction === 'asc' ? aValue - bValue : bValue - aValue
        }
        
        // Handle string sorting
        aValue = String(aValue).toLowerCase()
        bValue = String(bValue).toLowerCase()
        
        if (direction === 'asc') {
          return aValue > bValue ? 1 : -1
        } else {
          return aValue < bValue ? 1 : -1
        }
      })
    },
    
    // Toggle sorting when a column header is clicked
    toggleSort(key) {
      // Only allow sorting on sortable columns
      const column = this.columns.find(col => col.key === key)
      if (!column || !column.sortable) return
      
      // If clicking the same column, toggle direction
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        // New column, set as descending for count, ascending for others
        this.sortKey = key
        this.sortDirection = key === 'count' ? 'desc' : 'asc'
      }
      
      // Apply the new sorting
      this.applyFilters()
    },
    
    // Get the sort icon for a column
    getSortIcon(key) {
      if (this.sortKey !== key) return 'mdi mdi-sort'
      return this.sortDirection === 'asc' ? 'mdi mdi-sort-ascending' : 'mdi mdi-sort-descending'
    },
    
    // Get severity text from numeric value
    getSeverityText(severity) {
      if (severity === undefined || severity === null) return 'Unknown'
      
      // Convert to number if it's a string
      const severityNum = typeof severity === 'string' ? parseInt(severity, 10) : severity
      
      switch (severityNum) {
        case 4:
          return 'Critical'
        case 3:
          return 'High'
        case 2:
          return 'Medium'
        case 1:
          return 'Low'
        case 0:
          return 'Info'
        default:
          return 'Unknown'
      }
    },
    
    // Get severity class for badge styling
    getSeverityClass(severity) {
      if (severity === undefined || severity === null) return 'badge bg-secondary'
      
      // Convert to number if it's a string
      const severityNum = typeof severity === 'string' ? parseInt(severity, 10) : severity
      
      switch (severityNum) {
        case 4:
          return 'badge bg-danger'
        case 3:
          return 'badge bg-warning'
        case 2:
          return 'badge bg-info'
        case 1:
          return 'badge bg-success'
        case 0:
          return 'badge bg-secondary'
        default:
          return 'badge bg-secondary'
      }
    },
    
    // Get severity background color for charts
    getSeverityBackgroundColor(severity) {
      if (severity === undefined || severity === null) return '#6c757d'
      
      // Convert to number if it's a string
      const severityNum = typeof severity === 'string' ? parseInt(severity, 10) : severity
      
      switch (severityNum) {
        case 4:
          return '#dc3545' // danger - Critical
        case 3:
          return '#ffc107' // warning - High
        case 2:
          return '#17a2b8' // info - Medium
        case 1:
          return '#28a745' // success - Low
        case 0:
          return '#6c757d' // secondary - Info
        default:
          return '#6c757d' // secondary
      }
    },
    
    // Get text color for severity labels
    getSeverityTextColor(severity) {
      if (severity === undefined || severity === null) return '#6c757d'
      
      // Convert to number if it's a string
      const severityNum = typeof severity === 'string' ? parseInt(severity, 10) : severity
      
      switch (severityNum) {
        case 4:
          return '#dc3545' // danger - Critical
        case 3:
          return '#ffc107' // warning - High
        case 2:
          return '#17a2b8' // info - Medium
        case 1:
          return '#28a745' // success - Low
        case 0:
          return '#6c757d' // secondary - Info
        default:
          return '#6c757d' // secondary
      }
    },
    
    // Get color class for a specific severity name
    getSeverityColorClass(severityName) {
      switch (severityName.toLowerCase()) {
        case 'critical':
          return 'text-danger'
        case 'high':
          return 'text-warning'
        case 'medium':
          return 'text-info'
        case 'low':
          return 'text-success'
        case 'info':
          return 'text-secondary'
        default:
          return 'text-secondary'
      }
    },
    
    // Format CVSS score with N/A fallback
    formatCvssScore(score) {
      return score !== undefined && score !== null ? score.toFixed(1) : 'N/A'
    },
    downloadPdf () {
      const element = document.getElementById('pdfcontent')
      html2pdf(element, this.pdfConfig)
    },
    
    // Show vulnerability details modal
    showVulnerabilityDetails(vulnerability) {
      this.selectedVulnerability = vulnerability
      this.showVulnerabilityModal = true
      this.loadVulnerabilityDetails(vulnerability.plugin_id)
    },
    
    // Load vulnerability details from API
    loadVulnerabilityDetails(pluginId) {
      const baseURL = this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
      const url = `${baseURL}/tenable/vulnerabilities/${pluginId}/output`
      
      this.loadingVulnerabilityDetails = true
      this.vulnerabilityDetails = null
      
      axios.get(url)
        .then((response) => {
          console.log('Vulnerability details loaded successfully')
          if (response.data) {
            this.vulnerabilityDetails = response.data
          } else {
            console.error('No data in response')
            this.vulnerabilityDetails = null
          }
          this.loadingVulnerabilityDetails = false
        })
        .catch((error) => {
          console.error('Error fetching vulnerability details:', error)
          console.error('Error details:', {
            message: error.message,
            status: error.response && error.response.status,
            statusText: error.response && error.response.statusText,
            data: error.response && error.response.data,
            url: url
          })
          this.loadingVulnerabilityDetails = false
          this.vulnerabilityDetails = null
        })
    },
    
    // Close vulnerability details modal
    closeVulnerabilityModal() {
      this.showVulnerabilityModal = false
      this.selectedVulnerability = null
      this.vulnerabilityDetails = null
    },
    
    // Extract affected assets from the nested API response structure
    getAffectedAssets() {
      if (!this.vulnerabilityDetails || !this.vulnerabilityDetails.outputs) {
        return []
      }
      
      const assets = []
      
      // Navigate through the nested structure: outputs[].states[].results[].assets[]
      this.vulnerabilityDetails.outputs.forEach(output => {
        if (output.states && Array.isArray(output.states)) {
          output.states.forEach(state => {
            if (state.results && Array.isArray(state.results)) {
              state.results.forEach(result => {
                if (result.assets && Array.isArray(result.assets)) {
                  result.assets.forEach(asset => {
                    assets.push({
                      asset: asset,
                      port: result.port,
                      transport_protocol: result.transport_protocol,
                      application_protocol: result.application_protocol
                    })
                  })
                }
              })
            }
          })
        }
      })
      
      return assets
    },
    
    // Get plugin output from the first output in the response
    getPluginOutput() {
      if (!this.vulnerabilityDetails || !this.vulnerabilityDetails.outputs || this.vulnerabilityDetails.outputs.length === 0) {
        return null
      }
      
      return this.vulnerabilityDetails.outputs[0].plugin_output || null
    },
    
    // Format IPv6 addresses to be more readable and space-efficient
    formatIPv6(ipv6) {
      if (!ipv6) return 'N/A'
      
      // For very long IPv6 addresses, break them at colons for better display
      if (ipv6.length > 20) {
        return ipv6.replace(/(.{1,16}):/g, '$1:\n').replace(/\n$/, '')
      }
      
      return ipv6
    },
    
    // Format IPv6 addresses for compact display
    formatIPv6Short(ipv6) {
      if (!ipv6) return 'N/A'
      
      // Compress IPv6 by showing only first and last parts for very long addresses
      if (ipv6.length > 25) {
        const parts = ipv6.split(':')
        if (parts.length > 4) {
          return `${parts[0]}:${parts[1]}...${parts[parts.length-1]}`
        }
      }
      
      return ipv6
    },
    
    // Format dates in a more compact way
    formatDateShort(dateString) {
      if (!dateString) return 'N/A'
      
      const date = new Date(dateString)
      const now = new Date()
      const diffTime = Math.abs(now - date)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      // Show relative time for recent dates, absolute for older ones
      if (diffDays <= 30) {
        return `${diffDays}d ago`
      } else if (diffDays <= 365) {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      } else {
        return date.toLocaleDateString('en-US', { year: '2-digit', month: 'short' })
      }
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="container" style="max-width: 1420px !important;">
      <div class="row page-title align-items-center" />
      <div class="col-12">
        <b-button
          class="btn btn-primary btn-sm mr-1"
          variant="light"
          @click="downloadPdf"
        >
          Download PDF
        </b-button>
        <br><br>
        <div
          id="pdfcontent"
          style="width:1280px; break-after: page; clear: both;padding-left:20px;padding-right:20px;"
        >
          <section slot="pdf-content"></section>
            <div class="card">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center mb-4">
                  <h4 class="header-title">
                    Vulnerabilities from 
                    <span v-if="selectedSeverities.length === 0">All</span>
                    <span v-else-if="selectedSeverities.length === severityOptions.length">All</span>
                    <span v-else>
                      {{ selectedSeverities.join(', ') }}
                    </span>
                    Severities - Last {{ selectedDateRange }} Days
                  </h4>
                  <span>Updated: {{ updated }}</span>
                </div>
                
                <!-- Search and filter controls -->
                <div class="row mb-4">
                  <div class="col-md-4">
                    <div class="form-group">
                      <label class="form-label">Search Vulnerabilities</label>
                      <input 
                        type="text" 
                        class="form-control" 
                        v-model="filters.search" 
                        placeholder="Search by name or family"
                      >
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group">
                      <label class="form-label">Date Range</label>
                      <select class="form-select" v-model="selectedDateRange">
                        <option 
                          v-for="option in dateRangeOptions" 
                          :key="option.value" 
                          :value="option.value"
                        >
                          {{ option.text }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group">
                      <label class="form-label mb-2">Filter by Severity</label>
                      <div class="severity-checkboxes d-flex flex-nowrap align-items-center">
                        <div v-for="option in severityOptions" :key="option.value" class="form-check form-check-inline me-2">
                          <input 
                            type="checkbox" 
                            :id="`severity-${option.value}`" 
                            :value="option.value" 
                            v-model="selectedSeverities"
                            class="form-check-input"
                          >
                          <label :for="`severity-${option.value}`" class="form-check-label" :class="getSeverityColorClass(option.value)">
                            {{ option.text }}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Loading message -->
                <div v-if="loading" class="text-center py-5">
                  <p class="mt-2">Loading vulnerabilities...</p>
                </div>

                <!-- Chart section -->
                <div v-else-if="filteredVulnerabilities.length > 0" class="mb-5">
                  <h5 class="mb-3">Top 10 Vulnerabilities by Count</h5>
                  <div class="chart-container">
                    <apexchart
                      height="350"
                      :options="chartOptions"
                      :series="chartSeries"
                    ></apexchart>
                  </div>
                </div>
                
                <!-- Table content (only shown when not loading) -->
                <div v-if="!loading" class="table-responsive">
                  <!-- Debug info - can be removed after fixing -->
                  <div v-if="filteredVulnerabilities.length === 0" class="alert alert-info mb-3">
                    No vulnerability data received from API. Please check the console for more details.
                  </div>
                  
                  <div v-else-if="filteredVulnerabilities.length === 0" class="alert alert-warning mb-3">
                    No vulnerabilities match your search criteria. Try adjusting your filters.
                  </div>

                  <table class="table table-centered table-hover mb-0">
                    <thead>
                      <tr>
                        <th 
                          v-for="column in columns" 
                          :key="column.key" 
                          :class="{ 'sortable': column.sortable, 'active': sortKey === column.key }"
                          @click="column.sortable && toggleSort(column.key)"
                        >
                          {{ column.label }}
                          <i v-if="column.sortable" :class="getSortIcon(column.key)" class="sort-icon"></i>
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(vuln, index) in filteredVulnerabilities" :key="index">
                        <td><span :style="`color: ${getSeverityTextColor(vuln.severity)}; font-weight: 500;`">{{ vuln.plugin_name }}</span></td>
                        <td>{{ vuln.plugin_family }}</td>
                        <td>
                          <span :class="getSeverityClass(vuln.severity)">
                            {{ getSeverityText(vuln.severity) }}
                          </span>
                        </td>
                        <td><strong>{{ vuln.count }}</strong></td>
                        <td>{{ vuln.vulnerability_state }}</td>
                        <td>{{ formatCvssScore(vuln.cvss_base_score) }}</td>
                        <td>{{ formatCvssScore(vuln.cvss3_base_score) }}</td>
                        <td>
                          <button 
                            class="btn btn-sm btn-primary me-1" 
                            title="View Details"
                            @click="showVulnerabilityDetails(vuln)"
                          >
                            <i class="mdi mdi-eye"></i>
                            <span class="btn-text">Details</span>
                          </button>
                        </td>
                      </tr>
                      <tr v-if="filteredVulnerabilities.length === 0">
                        <td :colspan="columns.length +  1" class="text-center">No vulnerabilities found</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>

    <!-- Vulnerability Details Modal -->
    <b-modal
      v-model="showVulnerabilityModal"
      size="xl"
      :title="selectedVulnerability ? selectedVulnerability.plugin_name : 'Vulnerability Details'"
      hide-footer
      @hidden="closeVulnerabilityModal"
    >
      <div v-if="loadingVulnerabilityDetails" class="text-center py-4">
        <div class="spinner-border" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-2">Loading vulnerability details...</p>
      </div>

      <div v-else-if="vulnerabilityDetails" class="vulnerability-details">
        <!-- Basic Information -->
        <div class="row mb-4">
          <div class="col-md-6">
            <h5 class="text-primary mb-3">Basic Information</h5>
            <table class="table table-sm table-borderless">
              <tr>
                <td class="fw-bold text-muted">Plugin ID:</td>
                <td>{{ selectedVulnerability.plugin_id }}</td>
              </tr>
              <tr>
                <td class="fw-bold text-muted">Plugin Family:</td>
                <td>{{ selectedVulnerability.plugin_family }}</td>
              </tr>
              <tr>
                <td class="fw-bold text-muted">Severity:</td>
                <td>
                  <span :class="getSeverityClass(selectedVulnerability.severity)">
                    {{ getSeverityText(selectedVulnerability.severity) }}
                  </span>
                </td>
              </tr>
              <tr>
                <td class="fw-bold text-muted">CVSS Base Score:</td>
                <td>{{ formatCvssScore(selectedVulnerability.cvss_base_score) }}</td>
              </tr>
              <tr>
                <td class="fw-bold text-muted">CVSS3 Base Score:</td>
                <td>{{ formatCvssScore(selectedVulnerability.cvss3_base_score) }}</td>
              </tr>
              <tr>
                <td class="fw-bold text-muted">Count:</td>
                <td><strong>{{ selectedVulnerability.count }}</strong></td>
              </tr>
            </table>
          </div>
          <div class="col-md-6">
            <h5 class="text-primary mb-3">Vulnerability State</h5>
            <table class="table table-sm table-borderless">
              <tr>
                <td class="fw-bold text-muted">State:</td>
                <td>{{ selectedVulnerability.vulnerability_state }}</td>
              </tr>
            </table>
          </div>
        </div>

        <!-- Plugin Output Details -->
        <div v-if="getPluginOutput()" class="mb-4">
          <h5 class="text-primary mb-3">Plugin Output</h5>
          <div class="plugin-output bg-light p-3 rounded">
            <pre class="mb-0">{{ getPluginOutput() }}</pre>
          </div>
        </div>

        <!-- Description -->
        <div v-if="vulnerabilityDetails.description" class="mb-4">
          <h5 class="text-primary mb-3">Description</h5>
          <div class="description bg-light p-3 rounded">
            <div v-html="vulnerabilityDetails.description"></div>
          </div>
        </div>

        <!-- Solution -->
        <div v-if="vulnerabilityDetails.solution" class="mb-4">
          <h5 class="text-primary mb-3">Solution</h5>
          <div class="solution bg-light p-3 rounded">
            <div v-html="vulnerabilityDetails.solution"></div>
          </div>
        </div>

        <!-- See Also -->
        <div v-if="vulnerabilityDetails.see_also && vulnerabilityDetails.see_also.length > 0" class="mb-4">
          <h5 class="text-primary mb-3">See Also</h5>
          <div class="see-also bg-light p-3 rounded">
            <ul class="mb-0">
              <li v-for="(link, index) in vulnerabilityDetails.see_also" :key="index">
                <a :href="link" target="_blank" rel="noopener noreferrer">{{ link }}</a>
              </li>
            </ul>
          </div>
        </div>

        <!-- Affected Assets -->
        <div v-if="getAffectedAssets().length > 0" class="mb-4">
          <h5 class="text-primary mb-3">Affected Assets ({{ getAffectedAssets().length }})</h5>
          <div class="assets-table-container">
            <table class="table table-striped table-hover assets-table-compact">
              <thead class="table-dark">
                <tr>
                  <th class="col-hostname">Host</th>
                  <th class="col-fqdn">FQDN</th>
                  <th class="col-ip">IP</th>
                  <th class="col-netbios">NetBIOS</th>
                  <th class="col-dates">First/Last Seen</th>
                  <th class="col-port">Port</th>
                  <th class="col-service">Service</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(assetInfo, index) in getAffectedAssets()" :key="index">
                  <td class="col-hostname">
                    <span class="fw-bold text-primary asset-name">{{ truncateText(assetInfo.asset.hostname, 12) || 'N/A' }}</span>
                  </td>
                  <td class="col-fqdn">
                    <span class="fqdn-text" :title="assetInfo.asset.fqdn">{{ truncateText(assetInfo.asset.fqdn, 20) || 'N/A' }}</span>
                  </td>
                  <td class="col-ip">
                    <div class="ip-address-container">
                      <span v-if="assetInfo.asset.ipv4" class="ip-address">{{ assetInfo.asset.ipv4 }}</span>
                      <span v-else-if="assetInfo.asset.ipv6" class="ip-address ipv6">{{ formatIPv6Short(assetInfo.asset.ipv6) }}</span>
                      <span v-else class="text-muted">N/A</span>
                    </div>
                  </td>
                  <td class="col-netbios">
                    <span class="netbios-text">{{ truncateText(assetInfo.asset.netbios_name, 10) || 'N/A' }}</span>
                  </td>
                  <td class="col-dates">
                    <div class="date-container">
                      <div class="date-line">{{ formatDateShort(assetInfo.asset.first_seen) }}</div>
                      <div class="date-line">{{ formatDateShort(assetInfo.asset.last_seen) }}</div>
                    </div>
                  </td>
                  <td class="col-port">
                    <span class="port-number">{{ assetInfo.port || 'N/A' }}</span>
                  </td>
                  <td class="col-service">
                    <div class="service-container">
                      <div class="service-line">{{ truncateText(assetInfo.transport_protocol, 4) || 'N/A' }}</div>
                      <div class="service-line">{{ truncateText(assetInfo.application_protocol, 6) || 'N/A' }}</div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- No assets message -->
        <div v-else class="mb-4">
          <h5 class="text-primary mb-3">Affected Assets</h5>
          <div class="alert alert-info">
            No asset information available for this vulnerability.
          </div>
        </div>
      </div>

      <div v-else class="text-center py-4">
        <div class="alert alert-warning">
          Failed to load vulnerability details. Please try again.
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" @click="closeVulnerabilityModal">
          Close
        </button>
      </div>
    </b-modal>

  </Layout>
</template>

<style scoped>
.table th {
  font-weight: 600;
}

.table td {
  vertical-align: middle;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}

/* Sortable column styles */
.sortable {
  cursor: pointer;
  position: relative;
  user-select: none;
}

.sortable:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.sortable.active {
  background-color: rgba(0, 0, 0, 0.075);
}

.sort-icon {
  margin-left: 5px;
}

/* Filter controls */
.form-group {
  margin-bottom: 1rem;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: block;
}

.severity-checkboxes {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
}

.form-check-inline {
  margin-right: 0.5rem;
  white-space: nowrap;
}

.form-check-input:checked {
  background-color: #556ee6;
  border-color: #556ee6;
}

/* Chart styles */
.chart-container {
  margin: 0 auto;
  width: 100%;
}

.apexcharts-tooltip-box {
  padding: 8px 12px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  border-radius: 4px;
  color: #333;
}

.apexcharts-tooltip-title {
  font-weight: bold;
  margin-bottom: 5px;
  border-bottom: 1px solid #eee;
  padding-bottom: 3px;
}

/* Modal styles */
.vulnerability-details h5 {
  color: #556ee6;
  font-weight: 600;
  border-bottom: 2px solid #e3e7ee;
  padding-bottom: 8px;
}

.vulnerability-details .plugin-output pre {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.85rem;
  color: #333;
  max-height: 300px;
  overflow-y: auto;
}

.vulnerability-details .description,
.vulnerability-details .solution,
.vulnerability-details .see-also {
  font-size: 0.9rem;
  line-height: 1.6;
}

.vulnerability-details .table-sm td {
  padding: 0.5rem;
  border: none;
}

.vulnerability-details .table-sm td:first-child {
  width: 40%;
  white-space: nowrap;
}

.vulnerability-details .table-borderless td {
  border: none;
}

.vulnerability-details .assets-table {
  font-size: 0.85rem;
}

.vulnerability-details .assets-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  border-bottom: 2px solid #dee2e6;
}

.vulnerability-details .font-monospace {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9em;
  background-color: #f8f9fa;
  padding: 2px 4px;
  border-radius: 3px;
}

.modal-footer {
  border-top: 1px solid #dee2e6;
  padding: 1rem;
  background-color: #f8f9fa;
}

/* Scrollable modal content */
.modal-body {
  max-height: 70vh;
  overflow-y: auto;
}

/* Compact Assets Table */
.assets-table-container {
  width: 100%;
  overflow: visible;
}

.assets-table-compact {
  font-size: 0.75rem;
  margin-bottom: 0;
  table-layout: fixed;
  width: 100%;
}

.assets-table-compact th,
.assets-table-compact td {
  padding: 0.4rem 0.3rem;
  vertical-align: middle;
  word-wrap: break-word;
  overflow: hidden;
}

.assets-table-compact th {
  font-size: 0.7rem;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
}

/* Column-specific widths and styling */
.col-hostname { width: 12%; }
.col-fqdn { width: 22%; }
.col-ip { width: 13%; }
.col-netbios { width: 11%; }
.col-dates { width: 16%; }
.col-port { width: 8%; }
.col-service { width: 18%; }

/* Asset name styling */
.asset-name {
  font-size: 0.75rem;
  font-weight: 600;
}

/* FQDN styling */
.fqdn-text {
  font-size: 0.7rem;
  color: #495057;
  cursor: help;
}

/* IP Address styling */
.ip-address-container {
  text-align: center;
}

.ip-address {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.65rem;
  background-color: #e9ecef;
  padding: 1px 3px;
  border-radius: 2px;
  display: inline-block;
  color: #495057;
}

.ip-address.ipv6 {
  font-size: 0.6rem;
  word-break: break-all;
}

/* NetBIOS styling */
.netbios-text {
  font-size: 0.7rem;
  color: #6c757d;
  text-align: center;
  display: block;
}

/* Date container */
.date-container {
  text-align: center;
}

.date-line {
  font-size: 0.65rem;
  line-height: 1.1;
  color: #6c757d;
  margin: 1px 0;
}

.date-line:first-child {
  color: #28a745; /* Green for first seen */
  font-weight: 500;
}

.date-line:last-child {
  color: #dc3545; /* Red for last seen */
  font-weight: 500;
}

/* Port styling */
.port-number {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.7rem;
  font-weight: 600;
  color: #007bff;
  display: block;
  text-align: center;
}

/* Service container */
.service-container {
  text-align: center;
}

.service-line {
  font-size: 0.65rem;
  line-height: 1.1;
  margin: 1px 0;
  color: #495057;
}

.service-line:first-child {
  font-weight: 600;
  color: #6f42c1; /* Purple for transport protocol */
}

.service-line:last-child {
  color: #fd7e14; /* Orange for application protocol */
}

/* Action button styling */
.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  min-width: 60px;
}

.btn-sm .mdi {
  font-size: 1rem;
  margin-right: 4px;
}

.btn-sm .btn-text {
  font-size: 0.7rem;
}

/* If MDI icons are not loaded, hide the icon and show only text */
.btn-sm i.mdi:not([class*="mdi-"]):before {
  display: none;
}

/* Show both icon and text, but prioritize icon when available */
.btn-sm .mdi + .btn-text {
  display: inline;
}

/* If icon fonts fail to load, ensure button is still usable */
@supports not (font-family: 'Material Design Icons') {
  .btn-sm .mdi {
    display: none;
  }
  .btn-sm .btn-text {
    display: inline !important;
  }
}
</style>